<template>
  <div class="app" v-bind:class="{ 'dark-mode': currentTheme, 'dev-env': !currentEnv }" v-if="initialized" :key="containerKey()"
    :style="isSplitMode && isOpen ? { marginRight: effectiveSplitChatWidth + 'px' } : {}">
    <template>
      <AppHeader fixed>
        <b-link class="navbar-brand" to="/admin/dashboard">
          <Projects @project_changed="reRender()" />
        </b-link>
        <b-button v-if="$route.name === 'Home'" variant="primary" size="sm" class="ml-3" @click="showWorkspacesModal = true">
          <b-icon icon="building"></b-icon> Workspaces
        </b-button>
        <b-button v-else variant="link" size="sm" class="ml-3 home-dialog-btn" @click="showHomeDialog = true">
          <b-icon icon="grid-3x3-gap-fill" font-scale="1.2"></b-icon>
        </b-button>

        <b-navbar-nav class="ml-auto">
          <b-nav-item class="d-md-down-none" v-if="hasKortexAccess">
            <button class="kortex-btn" @click="openCortexChat" :title="$t('kortex.quickAccessHint')">
              <cortex-icon :size="14" css-class="kortex-btn__icon"></cortex-icon>
              <span class="kortex-btn__label">{{ $t('kortex.openChat') }}</span>
              <kbd class="kortex-kbd-hint">⌘⇧K</kbd>
            </button>
          </b-nav-item>
          <HeaderAlerts />
          <DefaultHeaderDropdownAccnt />
        </b-navbar-nav>
      </AppHeader>
      <div class="app-body">
        <notifications group="system" position="top right" :ignoreDuplicates="true" />
        <notifications group="dataChanged" position="bottom right">
          <template slot="body">
            <div class="vue-notification" @click="$router.go()">
              {{ $t('dataHasChanged') }}
            </div>
          </template>
        </notifications>
        <main-dialog title="We experienced unexpected error!" id="report-error-modal" ok-title="Send" ok-variant="success"
          @ok="sendErrorReport">
          <b-textarea style="min-height: 100px" :placeholder="this.$t('explainUs')"
            v-model="serverError.comment"></b-textarea>
        </main-dialog>
        <main class="main">

          <div class="container-fluid" style="margin-top:20px">
            <b-alert show v-if="doesntBelongToProject" variant="warning"> {{ $t('noRolesOnCurrentProject') }} </b-alert>
            <b-alert show v-else-if="noWorkspace" style="text-align: center">{{ $t("notBelongToWorkspace") }}
              {{ this.$store.getters.getCurrentProject.name }}. {{ $t("contactAdministrator") }} <b-button @click="logout"
                variant="primary"> {{ $t("logout") }} </b-button> </b-alert>
            <template v-else>
              <router-view></router-view>
            </template>
          </div>
        </main>
      </div>
      <WorkspacesModal
        :show="showWorkspacesModal"
        @close="showWorkspacesModal = false"
        @workspaceChanged="handleWorkspaceChanged"
      />
      <b-modal v-model="showHomeDialog" size="xl" hide-header hide-footer centered content-class="home-dialog-content">
        <div style="padding: 3rem;">
          <Home />
        </div>
      </b-modal>
      <TheFooter fixed>
        <!--footer-->

        <div class="mr-4">
          <c-switch color="protokol" variant="pill" :label="true" dataOn="live" dataOff="dev" v-model="currentEnv"
            v-if="this.$can('dev env_access') || this.$isAdmin()" />
        </div>

        <div class="clr-protokol-royal-purple">
          <a class="clr-protokol-royal-purple active" href="https://protokol.io">{{ $t('protokol') }}</a>
          <router-link to="/admin/platform/updates">
            <span class="ml-1 clr-protokol-royal-purple active">v{{ version }}</span>
          </router-link>
          <span class="ml-1">&copy; {{ getYear }}</span>
        </div>
        
        <div class="ml-auto" v-if="workspace">
          <span style="color: var(--text-secondary);">
            <b-icon icon="building"></b-icon> {{ workspace.name }}
          </span>
        </div>
        <!--End of Tawk.to Script-->

      </TheFooter>
    </template>
<!-- 
    <protokol-tutorial /> -->

    <Onboarding />

    <!-- Cortex AI Chat Drawer -->
    <chat-drawer />

    <!-- Kortex Split Panel -->
    <div v-if="hasKortexAccess && isSplitMode && isOpen" class="split-chat-panel" :style="{ width: effectiveSplitChatWidth + 'px' }">
      <div class="split-chat-resize-handle" @mousedown.stop="startSplitResize"></div>
      <chat-panel />
    </div>
  </div>
</template>

<style lang="scss">
.kortex-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 14px;
  background: rgba(108, 128, 255, 0.08);
  border: 1px solid rgba(108, 128, 255, 0.22);
  border-radius: 8px;
  color: rgba(108, 128, 255, 0.9);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  outline: none;
  position: relative;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(108, 128, 255, 0.14);
    border-color: rgba(108, 128, 255, 0.4);
    color: #8fa0ff;
    box-shadow: 0 0 12px rgba(108, 128, 255, 0.15);
  }

  &:active {
    transform: scale(0.97);
  }
}

.kortex-btn__icon {
  opacity: 0.85;
}

.kortex-btn__label {
  letter-spacing: 0.01em;
}

.kortex-kbd-hint {
  display: inline-block;
  margin-left: 2px;
  padding: 1px 5px;
  font-size: 10px;
  font-family: inherit;
  line-height: 1.4;
  color: rgba(108, 128, 255, 0.55);
  background: rgba(108, 128, 255, 0.06);
  border: 1px solid rgba(108, 128, 255, 0.15);
  border-radius: 4px;
  vertical-align: middle;
  letter-spacing: 0.02em;
  pointer-events: none;
}

.switch-protokol .switch-input:checked+.switch-slider {
  background: linear-gradient(135deg, #6c80ff, #764ba2);
  border: none;
}

.workspaceMarker {
  background-color: var(--text-primary);
  padding: 10px;
}

.workspaceMarkerActive {
  height: 50px;
}

.notification-bell {
  margin-right: 10px;
}

.dark-mode {
  .navbar-nav .nav-link:hover {
    color: #ccc;
  }

  .navbar-nav .nav-link.active {
    color: #ccc;
  }
}

/* ── Split-screen chat layout ── */
.split-chat-panel {
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 1029;
  display: flex;
  flex-direction: column;
  border-left: 1px solid var(--border-default, #2a2d3a);
  background: #1a1d27;
  overflow: hidden;
}

.split-chat-resize-handle {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: ew-resize;
  z-index: 10;
  background: transparent;
  transition: background 0.15s;
  &:hover {
    background: rgba(108, 128, 255, 0.2);
  }
}

.bookmark-item {
  // border-left: 1px solid rgba(255,255,255,0.5);
}

a.dropdown-item i.fa.fa-star-o {
  margin-left: 10px;
}

a.icon-holder {
  right: 10px;
  top: 5px;
  position: absolute;
  font-size: 20px;
  padding-left: 10px;
  padding-right: 10px;
  z-index: 10;
}

.sidebar-divider {
  display: block !important;
  background-color: var(--bg-card);
}

.home-dialog-content {
  background: var(--body-bg);
  border-radius: 16px;
  max-height: 80vh;
  overflow-y: auto;
}

.fa-desktop {
  &:hover {
    color: #6c80ff;
  }

  cursor: pointer;
  position: relative;
  margin-left: 15px;
}
</style>

<style scoped>
.main,
.app-footer {
  margin-left: 0px !important;
}

.home-dialog-btn {
  color: var(--text-primary);
  opacity: 0.7;
  transition: opacity 0.2s;
  text-decoration: none;

  &:hover {
    opacity: 1;
    color: #6c80ff;
    text-decoration: none;
  }
}
</style>

<script>
import nav from '@/_nav'
import { Header as AppHeader, SidebarToggler, Sidebar as AppSidebar, SidebarFooter, SidebarForm, SidebarHeader, SidebarMinimizer, SidebarNav, Breadcrumb, Aside as AppAside, AsideToggler, Footer as TheFooter } from '@/components/ui/vue/src'
import SidebarNavItem from '@/components/ui/vue/src'
import Projects from './Projects'
import WorkspacesModal from '../components/workflow/WorkspacesModal'
import DefaultHeaderDropdownAccnt from './DefaultHeaderDropdownAccnt'
import ProjectCreate from '../views/projects/Create'
import Changelog from '../../changelog.json'
import { Switch as cSwitch } from '@coreui/vue'
import Input from '@/components/form/Input'
import _ from 'lodash'
import { Howl } from 'howler';
import RenderEvent from '../components/integrations/RenderEvent'
import Onboarding from '@/views/dashboard/Onboarding'
import Home from '@/views/Home'
import claimsMixin from './claimsMixin'
import UnpaidInvoiceBanner from '@/components/billing/UnpaidInvoiceBanner'
import HeaderAlerts from '@/components/alerts/HeaderAlerts'
import ChatDrawer from '@/views/cortex/ChatDrawer'
import ChatPanel from '@/views/cortex/ChatPanel'

var DEFAULT_SPLIT_CHAT_WIDTH = 880
var MIN_SPLIT_CHAT_WIDTH = 720
var MAX_SPLIT_CHAT_VIEWPORT_RATIO = 0.72

function clampSplitChatWidth (width, viewportWidth) {
  var maxWidth = Math.floor(viewportWidth * MAX_SPLIT_CHAT_VIEWPORT_RATIO)
  if (maxWidth < MIN_SPLIT_CHAT_WIDTH) {
    return maxWidth
  }
  return Math.max(MIN_SPLIT_CHAT_WIDTH, Math.min(maxWidth, width))
}

export default {
  name: 'HomeContainer',
  mixins: [claimsMixin],
  components: {
    AsideToggler,
    AppHeader,
    AppSidebar,
    AppAside,
    TheFooter,
    Projects,
    WorkspacesModal,
    Breadcrumb,
    DefaultHeaderDropdownAccnt,
    SidebarForm,
    SidebarFooter,
    SidebarToggler,
    SidebarHeader,
    SidebarNav,
    SidebarMinimizer,
    ProjectCreate,
    cSwitch,
    InputField: Input,
    RenderEvent,
    SidebarNavItem,
    Onboarding,
    Home,
    UnpaidInvoiceBanner,
    HeaderAlerts,
    ChatDrawer,
    ChatPanel
  },
  data() {
    return {
      showWorkspacesModal: false,
      showHomeDialog: false,
      ready: false,
      componentsReady: false,
      doesntBelongToProject: false,
      noWorkspace: false,
      projectInfo: null,
      appUrl: null,
      createWorkspace: {
        modal: false,
        name: ''
      },
      errors: [],
      menu: [],
      initialized: false,
      subscriptionAlert: null,
      splitChatWidth: DEFAULT_SPLIT_CHAT_WIDTH,
      splitViewportWidth: typeof window !== 'undefined' ? window.innerWidth : Math.ceil(DEFAULT_SPLIT_CHAT_WIDTH / MAX_SPLIT_CHAT_VIEWPORT_RATIO),
      isSplitResizing: false,
      splitResizeStartX: 0,
      splitResizeStartWidth: 0
    }
  },
  computed: {
    apps() {
      const apps = this.$store.getters.getApps.map(app => {
        app.type = "platform-app"

        return app
      })

      const forgeApps = this.$store.getters.getForgeApps.map(app => {
        app.type = "forge-app"

        return app
      })

      return apps.concat(forgeApps)
    },

    bookmarks: {
      get() {
        let bookmarks = _.get(this.$store.getters.projectUserConfig("bookmarks"), "data", {})

        return Object.values(bookmarks).map(c => {
          return this.$store.getters.componentsMenu.find(f => {
            return c.name == f.name
          })
        }).filter(b => {
          if (!b) return false

          if (!b.is_active) {
            return false
          }

          if (b.is_public && this.$store.getters.isLiveEnv) {
            return true
          }

          return !this.$store.getters.isLiveEnv
        })
      },

      set(val) {
        let bookmarks = {}
        Object.values(val).forEach(v => {
          bookmarks[v.name] = v
        })

        this.$store.dispatch('updateProjectUserConfig', { path: "bookmarks", data: bookmarks })
      }
    },

    components() {
      let componentsMenu = this.$store.getters.componentsMenu.filter(c => {
        return c.is_public
      })

      let m = this.menu
      m = componentsMenu;

      m.sort((a, b) => {
        return a.sort - b.sort
      })

      return m
    },

    getYear() {
      return new Date().getFullYear()
    },

    version() {
      return Changelog[0].version
    },
    settingsPath() {
      return this.$route.meta.settingsPath
    },

    name() {
      return this.$route.name
    },
    list() {
      return this.$route.matched.filter((route) => {
        return route.name || route.meta.label
      })
    },

    workspaces() {
      return this.$store.getters.getCurrentProject.workspaces.filter(w => {
        return w.uuid != this.$store.getters.getCurrentWorkspace.uuid
      })
    },

    workspace() {
      return this.$store.getters.getCurrentWorkspace
    },

    sidebarNav() {
      let i = _.cloneDeep(nav.items).map(i => {
        i.originalName = i.name
        i.name = this.$t(i.name.toLowerCase())
        if (i.children) {
          i.children = i.children.map(c => {
            c.originalName = c.name
            c.name = this.$t(c.name.toLowerCase())
            return c
          })
        }

        return i
      })

      if (this.$can("manage base_components")) {
        i.push({
          name: this.$t('add'),
          url: '/admin/components/create',
          icon: 'icon-plus'
        })
      }

      i = i.filter(i => {
        if (i.integration && !this.$store.getters.isIntegrationActive(i.integration)) {
          return false
        }

        return true
      })

      return i
    },

    projects() {
      return this.$store.getters.projects
    },

    serverError() {
      return {
        comment: '',
        request_uuid: this.$store.getters.errorRequestID
      }
    },

    accountExpiresAt() {
      let user = this.$store.getters.getUser;

      if (user.expires_at === null || user.expires_at == "") {
        return null
      }

      return user.expires_at;
    },

    subscriptions() {
      return this.$store.getters.getSubscriptions
    },

    currentEnv: {
      get() {
        return this.$store.getters.getCurrentEnv == 'live'
      },

      set(val) {
        this.$store.dispatch("setCurrentEnv", val ? "live" : "dev").then(() => {
          this.reRender()

          if (this.$store.getters.getCurrentEnv == 'dev')
            this.$store.dispatch("initTutorial", "dev")
        })
      }
    },

    currentTheme: {
      get() {
        return this.$store.getters.getCurrentTheme == 'dark'
      },

      set(val) {
        this.$store.dispatch("setCurrentTheme", val ? "dark" : "light")
      }
    },

    currentWorkspace: {
      get() {
        return this.$store.getters.getCurrentWorkspace
      },

      set(val) {
        this.$store.dispatch("setWorkspace", val).then(() => {
          this.reRender()
        })
      }
    },

    currentWorkspaceName() {
      if (this.currentWorkspace !== undefined)
        return _.truncate(this.currentWorkspace.name, { length: 10 })

      return null
    },

    currentProjectName() {
      if (this.$store.getters.getCurrentProject) {
        return _.truncate(this.$store.getters.getCurrentProject.name, { length: 15 })
      }
    },

    newEvents() {
      return this.$store.getters.newEventsCount
    },

    userClaims() {
      return this.$store.getters.getClaims
    },

    isSplitMode: function () {
      return this.$store.state.cortex.isSplitMode
    },

    effectiveSplitChatWidth: function () {
      return clampSplitChatWidth(this.splitChatWidth, this.splitViewportWidth)
    },

    isOpen: function () {
      return this.$store.state.cortex.isOpen
    },

    hasKortexAccess: function () {
      if (!this.$store.getters.isIntegrationActive('protokol-kortex')) return false
      if (this.$isAdmin()) return true
      return this.$store.state.cortex.myAccess !== null
    }
  },

  methods: {
    openCortexChat: function () {
      if (!this.hasKortexAccess) return
      this.$store.dispatch('cortex/openChat')
    },

    startSplitResize: function (e) {
      this.isSplitResizing = true
      this.updateSplitViewportWidth()
      this.splitResizeStartX = e.clientX
      this.splitResizeStartWidth = this.splitChatWidth
      document.addEventListener('mousemove', this.onSplitResize)
      document.addEventListener('mouseup', this.stopSplitResize)
    },

    onSplitResize: function (e) {
      if (!this.isSplitResizing) return
      var delta = this.splitResizeStartX - e.clientX
      var newWidth = this.splitResizeStartWidth + delta
      this.splitChatWidth = clampSplitChatWidth(newWidth, this.splitViewportWidth)
    },

    stopSplitResize: function () {
      this.isSplitResizing = false
      document.removeEventListener('mousemove', this.onSplitResize)
      document.removeEventListener('mouseup', this.stopSplitResize)
    },

    updateSplitViewportWidth: function () {
      if (typeof window !== 'undefined') {
        this.splitViewportWidth = window.innerWidth
      }
    },

    handleHomeKeyboard: function (e) {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && (e.code === 'KeyH' || e.key === 'h' || e.key === 'H')) {
        e.preventDefault()
        this.showHomeDialog = !this.showHomeDialog
      }
    },

    handleKortexKeyboard: function (e) {
      if (!this.hasKortexAccess) return
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && !e.altKey && (e.code === 'KeyK' || e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        this.$store.dispatch('cortex/toggleQuickDialog')
      }
    },

    reRender() {
      this.$store.dispatch('refreshState')
      this.$store.commit("RELOAD_CONTAINER")
    },
    
    handleWorkspaceChanged(workspace) {
      this.reRender()
    },

    changeWorkspace(workspace) {
      this.currentWorkspace = workspace
    },

    logout() {
      this.$http.post("/v1/user/logout").then(resp => {
        this.$router.push('/login')
      }).catch(() => {
        this.$router.push('/login')
      })
    },

    containerKey() {
      return this.$store.getters.containerKey
    },

    displayDataChanged(data) {
      let currentComponentUUID = this.$store.getters.currentComponent.uuid
      let currentUser = this.$store.getters.getUser

      if ((data.data.component.uuid == currentComponentUUID)
        && data.user.uuid !== currentUser.uuid
      ) {

        this.$bvToast.toast("Data Changed", {
          variant: 'primary',
          autoHideDelay: 10000
        })
        
      }
    },

    sendErrorReport() {
      this.$http.post("/v1/system/report", this.serverError).then(resp => {
        this.$mainDialog.msgBoxOk(this.$t("thanksFeedback"))
      })
    },

    projectCreated() {
      this.$router.go('/admin/dashboard');
    },

    fieldError(error) {
      let found = this.errors.find((i, e) => {
        return i.field == error.field
      })

      if (found === undefined) {
        this.errors.push(error)
      } else {
        this.errors.map(e => {
          if (found.field == e.field)
            e.error = error.error
        })
      }
    },

    checkForErrors() {
      return this.errors.find((i, e) => {
        return i.error
      })
    },

    playNotification() {
      let notificationSettings = this.$store.getters.userConfig("notification_settings");
      if (!notificationSettings) return

      if (notificationSettings.data.play_sound) {
        var sound = new Howl({
          src: ['../sound/notification.wav']
        });

        sound.play();
      }
    },

    openComponent(component) {
      let ref = component.name
      if (component.scope) {
        ref = `${component.scope}/${ref}`
      }

      this.$router.push(`/home/component/${ref}`)
    },

    openApp(app) {
      window.open(this.$router.resolve(`/app/${app.name}`).href, "_blank")
    },

    isBookmarkActive(name) {
      return !!this.bookmarks.find(b => {
        return b.name == name
      });
    },

    bookmarkApp(e, app) {
      e.stopImmediatePropagation();
      let data = this.bookmarks || {};
      let bookmark = data.find(d => {
        return d.name == app.name
      })

      if (bookmark) {
        data = data.filter(d => {
          return d.name != bookmark.name
        })
      } else {
        data.push({
          name: app.name,
          url: app.url
        })
      }

      this.bookmarks = data
    },

    openBookmark(bookmark) {
      this.$router.push(bookmark.url)
    },

    openIntegration(int) {
      this.$router.push(`/home/integrations/page/${int.id}`)
    },

    bookmarkLabelTranslate(bookmark) {
      try {
        let component = this.$store.getters.components.find(c => {
          return c.name == bookmark.name
        })

        return this.$translateLabel(component.label) || bookmark.name
      } catch {
        // do nothing
        return bookmark.name
      }
    },

    backToDashboard() {
      this.$router.push("/admin/dashboard")
    }
  },

  async created() {
    this.$store.dispatch("fetchIntegrations");
    this.$store.dispatch("fetchTrials")

    try {
      await this.$setupComponents()

      this.componentsReady = true
      this.noWorkspace = false
    } catch (err) {
      console.error(err)
      
      if (err.response !== undefined) {
        if (err.response.status == 400) {
          this.doesntBelongToProject = true
          return
        }

        if (err.response.status >= 500) {
          this.$bvToast.toast(this.$t('unexpectedError'), {
            variant: 'danger'
          })
          return
        }

        if (err.response.status == 403) {
          this.componentsReady = true
        }
      }

      if (err == "noWorkspace" && this.$store.getters.currentWorkspace == null) {
        this.noWorkspace = true
        return
      }

      console.error(err)
      this.$bvToast.toast(this.$t('unexpectedError'), {
        variant: 'danger'
      })
    } finally {
      this.initialized = true
    }
  },

  async mounted() {
    this.updateSplitViewportWidth()
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.updateSplitViewportWidth)
    }
    document.addEventListener('keydown', this.handleHomeKeyboard)

    await this.$store.dispatch("fetchPermissions")

    this.$store.dispatch("fetchUserConfigs").then(() => {
      let data = this.$store.getters.userConfig('locale')
      let savedLocale = _.get(data[0], "data.locale", 'en_US')

      this.$store.commit('SET_LOCALE', savedLocale)
      this.$root.$i18n.locale = savedLocale
    }).catch(err => {
      console.log(err)
    })

    if (!this.$can('dev env_access')) {
      this.$store.dispatch("setCurrentEnv", "live")
    }

    if (this.$isAdmin()) {
      this.$store.dispatch("fetchPendingInvoices").catch(function () {})
    }

    this.$store.dispatch("fetchSubscriptions").then(data => {
      let expired = data.filter(d => {
        return d.is_expired
      })

      let cancelled = data.filter(d => {
        return d.is_canceled
      })

      if (cancelled.length > 0) {
        let msg = this.$t("cancelledSubscriptions", {
          services: cancelled.map(c => {
            return c.license_product.name
          }).join(",")
        })

        this.$bvToast.toast(msg, {
          variant: 'warning'
        })
      }

    }).catch(err => {
      this.$store.commit("SET_SUBSCRIPTIONS", []);
    })

    localStorage.setItem("run_mode", "platform");
    localStorage.setItem("current_theme", "dark");

    this.$store.dispatch("fetchNewEventsCount");

    this.$store.commit("SET_CURRENT_CONTEXT", "home")

    this.$store.dispatch("initTutorial", "introduction")

    if (this.$store.getters.getCurrentEnv == 'dev')
      this.$store.dispatch("initTutorial", "dev")

    if (!this.$isAdmin()) {
      this.$store.dispatch('cortex/loadMyAccess').catch(function () {})
    }
  },

  beforeDestroy: function () {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.updateSplitViewportWidth)
    }
    document.removeEventListener('mousemove', this.onSplitResize)
    document.removeEventListener('mouseup', this.stopSplitResize)
    document.removeEventListener('keydown', this.handleHomeKeyboard)
  }
}
</script>
